import { useCallback, useMemo } from "react";

import { useAppContext } from "@/app/context";
import { useRegistriesQuery } from "@/app/types/generated/graphql";

import { RegistryOption, UseRegistryDropdownFn } from "./registry-dropdown.types";

export const useRegistryDropdown: UseRegistryDropdownFn = () => {
  const { userAccountInfo, userSelections, setUserSelections } = useAppContext();
  const { loading, data: registriesData } = useRegistriesQuery({ fetchPolicy: "cache-first" });

  const linkedRegistriesOptions = useMemo(
    () =>
      (userAccountInfo?.linkedRegistries
        ?.map((linkedRegistry) => {
          const registry = registriesData?.registries?.find((registry) => registry.id === linkedRegistry.registryId);

          return registry
            ? {
                registryId: registry.id,
                registryCode: linkedRegistry.registryCode,
                shortName: registry.shortName,
                status: linkedRegistry.status,
              }
            : null;
        })
        .filter(Boolean) ?? []) as RegistryOption[],
    [registriesData?.registries, userAccountInfo?.linkedRegistries],
  );

  const activeRegistry = useMemo(
    () =>
      linkedRegistriesOptions?.find((registry) => registry?.registryId === userSelections?.registry?.registryId) ??
      null,
    [linkedRegistriesOptions, userSelections?.registry?.registryId],
  );

  const onActiveRegistryChange = useCallback(
    (registryOption: RegistryOption | null) => {
      const linkedRegistry = userAccountInfo?.linkedRegistries?.find(
        (linkedRegistry) => linkedRegistry.registryId === registryOption?.registryId,
      );

      if (!linkedRegistry) return;

      setUserSelections({
        ...userSelections,
        registry: linkedRegistry,
      });
    },
    [setUserSelections, userAccountInfo?.linkedRegistries, userSelections],
  );

  return {
    loading: loading,
    registries: linkedRegistriesOptions,
    activeRegistry,
    onActiveRegistryChange,
  };
};
